/**
 * AI Assistant Page
 * 
 * This page provides an interface for users to interact with an AI assistant
 * that can answer questions about procurement based on our knowledge base.
 * 
 * The page includes:
 * - A form for submitting questions
 * - Real-time response display
 * - Error handling
 * - Loading states
 */

import { useState, FormEvent } from 'react';
import { BasePage } from 'src/pages/BasePage/BasePage';
import { Breadcrumb } from 'src/components/Breadcrumb/Breadcrumb';
import { SectionTitle, SectionTitleSize, SectionTitleStyle } from 'src/components/SectionTitle/SectionTitle';
import { CMSSection, CMSSectionBackground } from 'src/components/CMSSection/CMSSection';
import { Text } from 'src/components/Text/Text';
import { API_URL } from '../util';

function AIAssistantPage() {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate prompt before proceeding
    if (!prompt.trim()) {
      setError('Please enter a question');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const params = new URLSearchParams();
      params.append('prompt', prompt.trim());

      console.log('Sending request to:', `${API_URL}search`)
      const response = await fetch(`${API_URL}search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString()
      });

      console.log('Response status:', response.status)
      console.log('Response headers:', Object.fromEntries(response.headers.entries()))
      
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType)
      
      if (contentType && contentType.includes('application/json')) {
        const json = await response.json();
        console.log('Received JSON response:', json)
        if (!response.ok) {
          throw new Error(json.error || json.message || `HTTP error! status: ${response.status}`);
        }
      } else {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const htmlContent = await response.text();
        console.log('Received HTML response:', htmlContent)
        const htmlMatch = htmlContent.match(/<html>[\s\S]*?<\/html>/i);
        console.log('HTML match:', htmlMatch)
        setResult(htmlMatch ? htmlMatch[0] : htmlContent);
      }
    } catch (err) {
      console.error('Error in handleSearch:', err)
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasePage>
      <Breadcrumb />
      
      <CMSSection
        primary={
          <>
            <SectionTitle title="AI Assistant" size={SectionTitleSize.Small} style={SectionTitleStyle.Dark} />
            <Text>Ask questions about procurement and get AI-generated responses based on our knowledge base.</Text>
            
            <form onSubmit={handleSearch} style={{ marginTop: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="prompt" style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Ask a question about procurement:
                </label>
                <input
                  id="prompt"
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., What are the key considerations when selecting a contractor?"
                  style={{ 
                    width: '100%', 
                    padding: '12px',
                    fontSize: '16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    transition: 'border-color 0.2s'
                  }}
                />
                <div style={{ 
                  marginTop: '8px',
                  fontSize: '14px',
                  color: '#666',
                  fontStyle: 'italic'
                }}>
                  Note: AI responses may take up to 90 seconds to generate. Please be patient while we process your request.
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{ 
                  minWidth: '120px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  transition: 'background-color 0.2s'
                }}
              >
                {loading ? 'Processing...' : 'Ask AI'}
              </button>
            </form>

            {error && (
              <div style={{ 
                padding: '24px', 
                marginTop: '24px',
                backgroundColor: '#ffebee',
                borderRadius: '8px',
                color: '#d32f2f',
                fontSize: '16px'
              }}>
                {error}
              </div>
            )}

            {result && (
              <div style={{ 
                marginTop: '24px'
              }}>
                <div style={{ 
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{ margin: 0, fontSize: '20px' }}>AI Response</h2>
                  <div style={{ 
                    fontSize: '14px',
                    color: '#666',
                    backgroundColor: '#f5f5f5',
                    padding: '4px 8px',
                    borderRadius: '4px'
                  }}>
                    AI Generated Content
                  </div>
                </div>
                <div style={{ 
                  width: '100%',
                  height: '600px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <iframe
                    srcDoc={result}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                    title="AI Response"
                  />
                </div>
              </div>
            )}
          </>
        }
        background={CMSSectionBackground.White}
      />
    </BasePage>
  );
}

export { AIAssistantPage }; 